import { useCallback, useMemo } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { Cuboid, useIsPointInsideCuboid, VisibilityBoxMode } from '@abyss/3d-viewer';
import { AnalysisCuboid } from '@/types';
import * as state from '@/components/Analysis/modules/cuboids/state';
import * as analysisState from '@/components/Analysis/state';
import * as poiState from '../../pointOfInterest/state';
import { useMakeStyledCuboid } from '@/modules/cuboids/hooks/useMakeStyledCuboid';
import { useCuboidState } from '../hooks/useCuboidState';
import { useCuboidStyleHandlers } from '../hooks/useCuboidStyleHandlers';
import { SceneCuboids as SceneCuboidsComponent } from '@/modules/cuboids/components/SceneCuboids';
import { useCuboidDataHandlers } from '../hooks/useCuboidDataHandlers';
import { useIsolateCuboidEffect } from '../hooks/useIsolateCuboid';

export const SceneCuboids = () => {
  const cuboids = useAtomValue(state.cuboids);

  const activeVisibilityBox = useAtomValue(analysisState.activeVisibilityBox);

  const setSelectedPointOfInterestId = useSetAtom(poiState.selectedPointOfInterestId);

  const arePoisVisible = useAtomValue(analysisState.arePoisVisible);

  const cuboidState = useCuboidState();
  const dataHandlers = useCuboidDataHandlers();
  const styleHandlers = useCuboidStyleHandlers();
  const makeStyledCuboid = useMakeStyledCuboid(styleHandlers);

  const onCuboidClick = useCallback(
    (cuboid: AnalysisCuboid) => {
      if (cuboid.poiId) {
        setSelectedPointOfInterestId(cuboid.poiId);
      }
    },
    [setSelectedPointOfInterestId]
  );

  const isPointInsideVisibilityBox = useIsPointInsideCuboid(
    activeVisibilityBox.mode === VisibilityBoxMode.Disabled ? undefined : activeVisibilityBox
  );

  const cuboidsComp = useMemo(() => {
    // Turning off VOI cuboids when POIs are not visible
    if (!arePoisVisible) return undefined;
    return cuboids
      .filterValues(({ position }) => isPointInsideVisibilityBox(position))
      .map((cuboid) => {
        return (
          <Cuboid
            {...makeStyledCuboid(cuboid)}
            key={cuboid.id}
            onClick={() => onCuboidClick(cuboid)}
            enableClippingByVisibilityBox={false}
          />
        );
      });
  }, [cuboids, makeStyledCuboid, onCuboidClick, arePoisVisible, isPointInsideVisibilityBox]);

  useIsolateCuboidEffect();

  return (
    <>
      {cuboidsComp}
      {/* SceneCuboidsComponent shows editing cuboids, current cuboid and editor */}
      <SceneCuboidsComponent
        cuboidState={cuboidState}
        dataHandlers={dataHandlers}
        styleHandlers={styleHandlers}
      />
    </>
  );
};
